import { useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { GitMerge, Shapes, TriangleAlert } from "lucide-react";
import { useApi } from "@ncp/api-client";
import { formatDate, formatNumber, useLocale, useTranslation } from "@ncp/i18n";
import { Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Field, NativeSelect, Skeleton, Textarea, useSave, useServerForm } from "@ncp/ui";

const TYPES = ["Horizontal", "Vertical", "Conglomerate"] as const;

interface MarketTypeReading {
  marketId: string; productMarket: string; productMarketAr?: string | null; applicantLabel: string; activeParties: number; bothPartiesActive: boolean;
  suggested?: string | null; conflict: boolean; readingEn: string; readingAr: string;
}
interface MarketClassification { marketId: string; productMarket: string; productMarketAr?: string | null; type: string; reasons: string; reasonsAr?: string | null }
interface TypeClassification { fileType: string; reasons: string; reasonsAr?: string | null; markets: MarketClassification[]; classifiedByName?: string | null; classifiedAt?: string | null }
interface ClassificationView {
  reading: { markets: MarketTypeReading[]; suggestedFileType?: string | null; anyConflict: boolean; readingEn: string; readingAr: string };
  recorded?: TypeClassification | null;
  complete: boolean;
  departsFromFigures: MarketTypeReading[];
}

/**
 * Scenario 6.10: the type of concentration recorded by the officer with written reasons, market by market and for the file – pre-filled from the
 * figures (horizontal only where both parties are active) with every applicant label the figures contradict flagged. The review report's §3 prints it.
 */
export function ConcentrationTypePanel({ caseId }: { caseId: string }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const ar = locale === "ar";
  const api = useApi();
  const qc = useQueryClient();
  const view = useQuery({ queryKey: ["ec", "classification", caseId], queryFn: () => api.get<ClassificationView>(`/api/ec/${caseId}/review/classification`) });
  // The classification refetched (an agent's reading, another officer's record) reaches only what the officer has not changed since (S 2.7).
  const recorded = useMemo<TypeClassification | null>(() => {
    const v = view.data;
    if (!v) return null;
    const r = v.recorded;
    return {
      fileType: r?.fileType ?? v.reading.suggestedFileType ?? "",
      reasons: r?.reasons ?? "",
      markets: v.reading.markets.map((m) => {
        const own = r?.markets.find((x) => x.marketId === m.marketId);
        return { marketId: m.marketId, productMarket: m.productMarket, productMarketAr: m.productMarketAr, type: own?.type ?? m.suggested ?? "", reasons: own?.reasons ?? "" };
      }),
    };
  }, [view.data]);
  const [draft, setDraft] = useServerForm<TypeClassification | null>(recorded, null, caseId);

  const save = useSave({
    mutationFn: (body: TypeClassification) => api.put<ClassificationView>(`/api/ec/${caseId}/review/classification`, body),
    saved: t("backoffice:concentrationStructure.saved"),
    failed: t("backoffice:concentrationStructure.notSaved"),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["ec", "classification", caseId] });
      void qc.invalidateQueries({ queryKey: ["case-review", caseId] });
    },
  });

  if (view.isLoading) return <Skeleton className="h-40" />;
  const v = view.data;
  if (!v || !draft) return null;
  const typeLabel = (type?: string | null) => (type ? t(`backoffice:concentrationStructure.types.${type}`, { defaultValue: type }) : t("backoffice:concentrationStructure.types.none"));
  const market = (id: string) => v.reading.markets.find((m) => m.marketId === id);
  const setMarket = (id: string, patch: Partial<MarketClassification>) => setDraft((d) => (d ? { ...d, markets: d.markets.map((m) => (m.marketId === id ? { ...m, ...patch } : m)) } : d));

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2 text-base"><Shapes className="size-4 text-aegold-700" /> {t("backoffice:concentrationStructure.typeTitle")}</CardTitle>
          {v.recorded ? (
            <Badge size="sm" variant={v.complete ? "success" : "warning"}>
              {v.complete ? t("backoffice:concentrationStructure.recorded", { type: typeLabel(v.recorded.fileType), name: v.recorded.classifiedByName ?? "—", date: v.recorded.classifiedAt ? formatDate(v.recorded.classifiedAt, locale) : "—" }) : t("backoffice:concentrationStructure.incomplete")}
            </Badge>
          ) : <Badge size="sm" variant="outline">{t("backoffice:concentrationStructure.notRecorded")}</Badge>}
        </div>
        <CardDescription>{t("backoffice:concentrationStructure.typeHint")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="rounded-lg bg-sand-50 p-3 text-xs text-sand-800" dir="auto">{ar ? v.reading.readingAr : v.reading.readingEn}</p>
        {v.departsFromFigures.length ? (
          <p className="flex items-start gap-1.5 text-xs text-warning-800"><TriangleAlert className="mt-0.5 size-3.5 shrink-0" /> {t("backoffice:concentrationStructure.departs", { markets: v.departsFromFigures.map((m) => (ar && m.productMarketAr ? m.productMarketAr : m.productMarket)).join(ar ? "، " : ", ") })}</p>
        ) : null}
        <div className="space-y-3">
          {draft.markets.map((m) => {
            const r = market(m.marketId);
            return (
              <div key={m.marketId} className="rounded-lg border border-sand-200 p-3">
                <div className="mb-2 flex flex-wrap items-center gap-2 text-sm">
                  <span className="font-medium" dir="auto">{ar && m.productMarketAr ? m.productMarketAr : m.productMarket}</span>
                  <Badge size="sm" variant="outline">{t("backoffice:concentrationStructure.applicantLabel", { type: typeLabel(r?.applicantLabel) })}</Badge>
                  <Badge size="sm" variant="outline">{r?.bothPartiesActive ? t("backoffice:concentrationStructure.bothActive") : t("backoffice:concentrationStructure.notBothActive")}</Badge>
                  {r?.conflict ? <Badge size="sm" variant="warning">{t("backoffice:concentrationStructure.conflict")}</Badge> : null}
                </div>
                {r?.conflict ? <p className="mb-2 text-xs text-warning-800" dir="auto">{ar ? r.readingAr : r.readingEn}</p> : null}
                <div className="grid gap-3 md:grid-cols-[220px_1fr]">
                  <Field label={t("backoffice:concentrationStructure.type")}>{(id) => (
                    <NativeSelect id={id} value={m.type} onChange={(e) => setMarket(m.marketId, { type: e.target.value })}>
                      <option value="">{t("backoffice:concentrationStructure.choose")}</option>
                      {TYPES.map((type) => <option key={type} value={type}>{typeLabel(type)}</option>)}
                    </NativeSelect>
                  )}</Field>
                  <Field label={t("backoffice:concentrationStructure.reasons")}>{(id) => <Textarea id={id} rows={2} dir="auto" value={m.reasons} onChange={(e) => setMarket(m.marketId, { reasons: e.target.value })} draft={`case:${caseId}:classification:market:${m.marketId}:reasons`} />}</Field>
                </div>
              </div>
            );
          })}
        </div>
        <div className="grid gap-3 md:grid-cols-[220px_1fr]">
          <Field label={t("backoffice:concentrationStructure.fileType")}>{(id) => (
            <NativeSelect id={id} value={draft.fileType} onChange={(e) => setDraft({ ...draft, fileType: e.target.value })}>
              <option value="">{t("backoffice:concentrationStructure.choose")}</option>
              {TYPES.map((type) => <option key={type} value={type}>{typeLabel(type)}</option>)}
            </NativeSelect>
          )}</Field>
          <Field label={t("backoffice:concentrationStructure.fileReasons")} hint={t("backoffice:concentrationStructure.reasonsHint")}>{(id) => <Textarea id={id} rows={3} dir="auto" value={draft.reasons} onChange={(e) => setDraft({ ...draft, reasons: e.target.value })} draft={`case:${caseId}:classification:reasons`} />}</Field>
        </div>
        <div className="flex justify-end">
          <Button loading={save.isPending} onClick={() => save.mutate(draft)}>{t("backoffice:concentrationStructure.save")}</Button>
        </div>
      </CardContent>
    </Card>
  );
}

interface JvActivity { market: string; marketAr?: string | null; relation: string; sharePercent?: number | null; note?: string | null }
interface JvParent { name: string; nameAr?: string | null; stakePercent?: number | null; controlRights: string[]; activities: JvActivity[]; noOtherActivity: boolean }
interface JointVentureView {
  jointVenture?: { name: string; nameAr?: string | null; activities?: string | null; activitiesAr?: string | null; parents: JvParent[] } | null;
  fullFunction?: boolean | null;
  coordination: {
    applies: boolean; declared: boolean; jointControl: boolean; controllingParents: string[];
    markets: Array<{ market: string; marketAr?: string | null; relation: string; parents: Array<{ name: string; nameAr?: string | null; sharePercent?: number | null }>; partnersSharePercent?: number | null; channelsEn: string[]; channelsAr: string[] }>;
    missingEn: string[]; missingAr: string[]; readingEn: string; readingAr: string;
  };
}

/**
 * Scenario 6.9: the joint venture and its parent companies as filed – each parent's stake, control rights and activities – and the platform's check of
 * coordination between the parents: every market where two or more of them remain active, with their shares and the channels for coordination.
 * Shown on joint-venture files only.
 */
export function JointVenturePanel({ caseId }: { caseId: string }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const ar = locale === "ar";
  const api = useApi();
  const q = useQuery({ queryKey: ["ec", "joint-venture", caseId], queryFn: () => api.get<JointVentureView>(`/api/ec/${caseId}/review/joint-venture`) });
  const d = q.data;
  if (!d?.coordination.applies) return null;
  const c = d.coordination;
  const jv = d.jointVenture;
  const pct = (v?: number | null) => (v == null ? "—" : `${formatNumber(v, locale, { maximumFractionDigits: 2 })}%`);
  const name = (en: string, arName?: string | null) => (ar && arName ? arName : en);
  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2 text-base"><GitMerge className="size-4 text-aegold-700" /> {t("backoffice:concentrationStructure.jvTitle")}</CardTitle>
          <div className="flex flex-wrap gap-2">
            {d.fullFunction != null ? <Badge size="sm" variant="outline">{d.fullFunction ? t("backoffice:concentrationStructure.fullFunction") : t("backoffice:concentrationStructure.notFullFunction")}</Badge> : null}
            {c.declared ? <Badge size="sm" variant={c.jointControl ? "outline" : "warning"}>{c.jointControl ? t("backoffice:concentrationStructure.jointControl") : t("backoffice:concentrationStructure.controlUnclear")}</Badge> : null}
            <Badge size="sm" variant={!c.declared ? "danger" : c.markets.length ? "warning" : "success"}>{!c.declared ? t("backoffice:concentrationStructure.notFiled") : c.markets.length ? t("backoffice:concentrationStructure.spillOver", { count: c.markets.length }) : t("backoffice:concentrationStructure.noSpillOver")}</Badge>
          </div>
        </div>
        {jv ? <CardDescription dir="auto">{name(jv.name, jv.nameAr)}{jv.activities ? ` · ${ar && jv.activitiesAr ? jv.activitiesAr : jv.activities}` : ""}</CardDescription> : null}
      </CardHeader>
      <CardContent className="space-y-4">
        {jv?.parents.length ? (
          <div className="overflow-x-auto" tabIndex={0}>
            <table className="w-full text-sm">
              <thead><tr className="border-b border-sand-200 text-xs text-sand-600">
                <th className="px-2 py-2 text-start font-semibold">{t("backoffice:concentrationStructure.parent")}</th>
                <th className="px-2 py-2 text-end font-semibold">{t("backoffice:concentrationStructure.stake")}</th>
                <th className="px-2 py-2 text-start font-semibold">{t("backoffice:concentrationStructure.controlRights")}</th>
                <th className="px-2 py-2 text-start font-semibold">{t("backoffice:concentrationStructure.activities")}</th>
              </tr></thead>
              <tbody>
                {jv.parents.map((p) => (
                  <tr key={p.name} className="border-b border-sand-100 align-top">
                    <td className="px-2 py-2"><bdi className="font-medium">{name(p.name, p.nameAr)}</bdi></td>
                    <td className="px-2 py-2 text-end tabular">{pct(p.stakePercent)}</td>
                    <td className="px-2 py-2 text-xs">{p.controlRights.length ? p.controlRights.map((r) => t(`backoffice:concentrationStructure.rights.${r}`, { defaultValue: r })).join(ar ? "، " : ", ") : "—"}</td>
                    <td className="px-2 py-2 text-xs">{p.activities.length ? p.activities.map((a) => `${name(a.market, a.marketAr)} (${t(`backoffice:concentrationStructure.relations.${a.relation}`, { defaultValue: a.relation })}${a.sharePercent != null ? ` · ${pct(a.sharePercent)}` : ""})`).join(ar ? "؛ " : "; ") : p.noOtherActivity ? t("backoffice:concentrationStructure.noOtherActivity") : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
        {c.markets.length ? (
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wider text-sand-600">{t("backoffice:concentrationStructure.marketsAtRisk")}</p>
            {c.markets.map((m) => (
              <div key={m.market} className="rounded-lg border border-warning-200 bg-warning-50 p-3 text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium" dir="auto">{name(m.market, m.marketAr)}</span>
                  <Badge size="sm" variant="outline">{t(`backoffice:concentrationStructure.relations.${m.relation}`, { defaultValue: m.relation })}</Badge>
                  <span className="text-xs text-sand-800">{m.parents.map((p) => `${name(p.name, p.nameAr)} ${pct(p.sharePercent)}`).join(ar ? "، " : ", ")}{m.partnersSharePercent != null ? ` · ${t("backoffice:concentrationStructure.together", { share: pct(m.partnersSharePercent) })}` : ""}</span>
                </div>
                <ul className="mt-1 list-disc space-y-0.5 ps-5 text-xs text-sand-800">{(ar ? m.channelsAr : m.channelsEn).map((ch) => <li key={ch}>{ch}</li>)}</ul>
              </div>
            ))}
          </div>
        ) : null}
        {c.missingEn.length ? <p className="flex items-start gap-1.5 text-xs text-warning-800"><TriangleAlert className="mt-0.5 size-3.5 shrink-0" /> {t("backoffice:concentrationStructure.missing", { items: (ar ? c.missingAr : c.missingEn).join(ar ? "؛ " : "; ") })}</p> : null}
        <p className="rounded-lg bg-sand-50 p-3 text-xs text-sand-800" dir="auto">{ar ? c.readingAr : c.readingEn}</p>
        <p className="text-xs text-muted-foreground">{t("backoffice:concentrationStructure.jvCriterion")}</p>
      </CardContent>
    </Card>
  );
}
